import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CUSTOMERENVIRONMENT, WINDOW } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { AlertTemplatesComponent } from '@app/components/alert-templates/alert-templates.component';
import { AuthService } from '@app/services/auth.service';
import { HubConnection, HubConnectionState } from '@microsoft/signalr/dist/esm/HubConnection';
import { HubConnectionBuilder } from '@microsoft/signalr/dist/esm/HubConnectionBuilder';
import { LogLevel } from '@microsoft/signalr/dist/esm/ILogger';
import { Environment } from '@environments/interface.environment';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { finalize, map, mergeMap, publishReplay, refCount } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {

  private hubConnection: HubConnection;
  private innerSubject$ = new BehaviorSubject(null);

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment, @Inject(WINDOW) private window: Window, private authService: AuthService, private snackBar: MatSnackBar) {

    this.hubConnection = new HubConnectionBuilder()
      // use our default debug url to the API here. The proxy wont do websockets.
      .withUrl(`${this.environment.production || this.window.location.port !== '4200' ? '' : 'http://localhost:64818'}/hub`, {
        accessTokenFactory: () => {
          const scopes = this.authService.getScopesForEndpoint('/api/');
          if (scopes) {
            return this.authService
              .acquireTokenSilentWithFallBackToPopup({ ...scopes, ...{ account: this.authService.accountInfo } }, scopes)
              .pipe(
                map(authResult => authResult.accessToken))
              .toPromise();
          }
        }
      })
      .configureLogging(this.environment.production ? LogLevel.Information : LogLevel.Debug)
      .withAutomaticReconnect()
      .build();

    this.hubConnection.onclose((err) => {
      if (err && err.message.indexOf('1006') !== -1) {
        // Show snackbar or something indicating a closed connection from the server.
        Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, err.message, 5000);
      }
      this.innerSubject$.complete();
    });

    this.hubConnection.serverTimeoutInMilliseconds = 60000;
    this.hubConnection.keepAliveIntervalInMilliseconds = 30000;
  }

  /** Will start the SignalR connection and stop it on 'unsubscribe'. Pattern stolen from this example https://github.com/ReactiveX/rxjs/issues/3786 */
  connect(): Observable<HubConnection> {
    return this.innerSubject$
      .pipe(
        mergeMap(() => {
          if (this.hubConnection.state === HubConnectionState.Connected) {
            return of(this.hubConnection);
          } else {
            return from(this.hubConnection.start())
              .pipe(
                map(() => {
                  if (this.hubConnection.state === HubConnectionState.Connected) {
                    return this.hubConnection;
                  } else {
                    return null;
                  }
                }));
          }
        }),
        finalize(() => {
          // Stop the connection on unsubscribe.
          this.hubConnection.off('UpdateComponentState');

          this.hubConnection.stop()
            .then(() => {
              console.log('SignalR connection was stopped!');
            })
            .catch((err) => {
              console.log('SignalR connection caught error when trying to stop: ' + err);
            });
        }),
        publishReplay(1),
        refCount()
      );
  }
}
